import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'debug'
  message: string
  durationSeconds?: number | null
  onDismiss?: () => void
}

const isToastDebugEnabled = import.meta.env.VITE_TOAST_DEBUG === 'true'

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<Toast[]>([])

  const addToast = (toast: Omit<Toast, 'id'>) => {
    if (toast.type === 'debug' && !isToastDebugEnabled) {
      return
    }
    const id = Date.now().toString()
    const newToast: Toast = {
      id,
      ...toast,
      durationSeconds:
        toast.durationSeconds !== undefined ? toast.durationSeconds : 5,
    }

    toasts.value.push(newToast)

    // Auto-remove toast after duration
    if (newToast.durationSeconds && newToast.durationSeconds > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.durationSeconds * 1000)
    }
  }

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex((toast) => toast.id === id)
    if (index === -1) return

    const [removedToast] = toasts.value.splice(index, 1)
    removedToast?.onDismiss?.()
  }

  const success = (message: string, durationSeconds?: number | null) => {
    addToast({ type: 'success', message, durationSeconds })
  }

  const error = (message: string, durationSeconds?: number | null) => {
    addToast({ type: 'error', message, durationSeconds })
  }

  const warning = (message: string, durationSeconds?: number | null) => {
    addToast({ type: 'warning', message, durationSeconds })
  }

  const info = (message: string, durationSeconds?: number | null) => {
    addToast({ type: 'info', message, durationSeconds })
  }

  const debug = (message: string, durationSeconds: number = 30) => {
    addToast({ type: 'debug', message, durationSeconds: durationSeconds })
  }

  return {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    debug,
  }
})
